<template>
  <!-- Dialog Wrapper -->
  <div>
    <!-- Backdrop -->
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-black/40 backdrop-blur-[1px] z-[9998]"
      @click="onBackdropClick"
    ></div>

    <!-- Dialog Panel -->
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[10001] flex items-start sm:items-center justify-center p-4 sm:p-6"
      aria-modal="true"
      role="dialog"
    >
      <div class="w-full max-w-5xl bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 border-b">
          <h3 class="text-base font-semibold text-gray-800">{{ dialogTitle }}</h3>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
            @click="closeDialog"
            aria-label="Close dialog"
          >
            ✕
          </button>
        </div>

        <!-- Body -->
        <div class="p-4 w-full">
          <div class="w-full">
    <!-- Search -->
    <div class="mb-3 flex items-center gap-2">
      <input
        v-model="searchQuery"
        type="text"
        class="w-full border border-gray-300 rounded-md px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        :placeholder="searchPlaceholder"
      />
      <button
        v-if="searchQuery"
        @click="clearSearch"
        class="text-sm text-gray-500 hover:text-gray-700 px-2 py-1"
        title="Clear"
      >
        ✕
      </button>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto border border-gray-200 rounded-md" style="max-height: 60vh">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th
              v-for="h in headers"
              :key="h.key"
              scope="col"
              class="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider"
            >
              {{ h.label ?? h.key }}
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <tr
            v-for="(row, idx) in paginatedRows"
            :key="rowKey(row, idx)"
            @click="onRowClick(row)"
            class="hover:bg-blue-50 cursor-pointer"
          >
            <td
              v-for="h in headers"
              :key="h.key"
              class="px-4 py-2 whitespace-nowrap text-sm text-gray-700"
            >
              {{ formatCell(row, h.key) }}
            </td>
          </tr>
          <tr v-if="paginatedRows.length === 0">
            <td :colspan="headers.length" class="px-4 py-6 text-center text-sm text-gray-500">
              No results found
            </td>
          </tr>
        </tbody>
      </table>
    </div>

            <!-- Pagination -->
            <div class="mt-4 flex items-center justify-between gap-2">
      <div class="text-xs text-gray-600">
        <span v-if="totalItems > 0">
          表示中 {{ startItem + 1 }}-{{ endItem }} の {{ totalItems }}
        </span>
        <span v-else>
          0 results
        </span>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="px-3 py-1 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          :disabled="currentPage === 1"
          @click="prevPage"
        >
          ‹ 前へ
        </button>
        <span class="text-sm text-gray-700">
          ページ {{ currentPage }} / {{ totalPages || 1 }}
        </span>
        <button
          class="px-3 py-1 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          :disabled="currentPage === totalPages || totalPages === 0"
          @click="nextPage"
        >
          次 ›
        </button>
        <select
          v-model.number="internalPageSize"
          class="ml-2 border rounded-md text-sm px-2 py-1"
        >
          <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
            </select>
          </div>
        </div>
          </div>

          <!-- Footer (optional slot for actions) -->
          <div class="mt-4 flex items-center justify-end gap-2 border-t pt-3">
            <slot name="footer">
              <button
                type="button"
                class="px-3 py-1.5 rounded-md border text-sm hover:bg-gray-50"
                @click="closeDialog"
              >
                キャンセル
              </button>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
  
</template>

<script>
export default {
  name: 'CCommonDialogComponent',
  emits: ['row-click', 'open', 'close'],
  props: {
    headers: {
      type: Array,
      required: true, // [{ key: 'name', title: 'Name' }]
    },
    data: {
      type: Array,
      default: () => [],
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    pageSizeOptions: {
      type: Array,
      default: () => [5, 10, 20, 50],
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...'
    },
    dialogTitle: {
      type: String,
      default: '項目を選択してください'
    },
    closeOnBackdrop: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      currentPage: 1,
      internalPageSize: this.pageSize,
    };
  },
  computed: {
    normalizedHeaders() {
      // Ensure each header has a key
      return (this.headers || []).filter(h => h && h.key);
    },
    filteredRows() {
      if (!this.searchQuery) return this.data || [];
      const q = String(this.searchQuery).toLowerCase();
      return (this.data || []).filter(row => {
        return this.normalizedHeaders.some(h => {
          const v = row?.[h.key];
          if (v === null || v === undefined) return false;
          return String(v).toLowerCase().includes(q);
        });
      });
    },
    totalItems() {
      return this.filteredRows.length;
    },
    totalPages() {
      if (this.totalItems === 0) return 0;
      return Math.ceil(this.totalItems / this.internalPageSize);
    },
    startItem() {
      if (this.totalItems === 0) return 0;
      return (this.currentPage - 1) * this.internalPageSize;
    },
    endItem() {
      return Math.min(this.startItem + this.internalPageSize, this.totalItems);
    },
    paginatedRows() {
      if (this.totalItems === 0) return [];
      const start = this.startItem;
      return this.filteredRows.slice(start, start + this.internalPageSize);
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    data: {
      handler() {
        // Keep the current page within bounds if data changes
        if (this.currentPage > this.totalPages) {
          this.currentPage = Math.max(1, this.totalPages);
        }
      },
      deep: true,
    },
    internalPageSize() {
      this.currentPage = 1;
    }
  },
  methods: {
    // Public API — can be called from parent via $refs
    openDialog() {
      if (!this.isOpen) {
        this.isOpen = true;
        this.$emit('open');
      }
    },
    closeDialog() {
      if (this.isOpen) {
        this.isOpen = false;
        this.$emit('close');
      }
    },
    onBackdropClick() {
      if (this.closeOnBackdrop) {
        this.closeDialog();
      }
    },
    clearSearch() {
      this.searchQuery = '';
    },
    rowKey(row, idx) {
      // Try to find an id-like key, otherwise fallback to index
      const id = row?.id ?? row?.key ?? row?._id;
      return id ?? idx;
    },
    formatCell(row, key) {
      const v = row?.[key];
      if (v === null || v === undefined) return '';
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
    },
    onRowClick(row) {
      this.$emit('row-click', row);
    },
    goToPage(p) {
      if (this.totalPages === 0) return;
      const clamped = Math.max(1, Math.min(this.totalPages, p));
      this.currentPage = clamped;
    },
    prevPage() {
      this.goToPage(this.currentPage - 1);
    },
    nextPage() {
      this.goToPage(this.currentPage + 1);
    }
  }
}
</script>

<style lang="css" scoped>
/* No additional styles; uses Tailwind utility classes */
</style>